import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, computed, inject, Input, input, signal, Signal, WritableSignal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IcfBarPercentComponent } from '../../../../components/icf-bar-percent/icf-bar-percent.component';
import { AratScorePercentPipe } from '../../../../pipes/arat-score-percent.pipe';
import { AssessmentNotesPipe } from '../../../../pipes/assessment-notes.pipe';
import { AssessmentScorePercentPipe } from '../../../../pipes/assessment-score-percent.pipe';
import { AssessmentScoreTargetDiffPipe } from '../../../../pipes/assessment-score-target-diff.pipe';
import { AssessmentScoreTargetPipe } from '../../../../pipes/assessment-score-target.pipe';
import { AssessmentScorePipe } from '../../../../pipes/assessment-score.pipe';
import { BbsScorePipe } from '../../../../pipes/bbs-score.pipe';
import { DateRelativPipe } from '../../../../pipes/datum-relativ.pipe';
import { NhptScorePipe } from '../../../../pipes/nhpt-score.pipe';
import {
  Assessment,
  AssessmentChartDataRowMixed,
  AssessmentType,
  CustomAssessment,
  CustomData,
  CustomFile,
  NhptData,
  SingleNumberDataSet,
  SubTestDataSet,
  TugData,
} from '../../../../services/model';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';

@Component({
  selector: 'app-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButton,
    MatIcon,
    DateRelativPipe,
    DatePipe,
    IcfBarPercentComponent,
    AssessmentScorePipe,
    AssessmentScoreTargetPipe,
    AssessmentScorePercentPipe,
    AssessmentScoreTargetDiffPipe,
    AratScorePercentPipe,
    AssessmentNotesPipe,
    NhptScorePipe,
    BbsScorePipe,
    MatCard,
  ],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss',
})
export class DetailsComponent {
  data = input.required<AssessmentChartDataRowMixed>();
  @Input() assessmentList!: WritableSignal<Assessment[]>;
  @Input() customAssessment?: CustomAssessment;
  assessmentFilteredList = computed(() => {
    if (this.assessmentList) {
      const result = this.assessmentList().filter(element => {
        if (this.isCustom() && element.type === 'CUSTOM') {
          return element.customId === this.customAssessment?.id;
        } else {
          return element.type === this.type();
        }
      });
      return result;
    } else {
      return [];
    }
  });

  private location: Location = inject(Location);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);

  private initPhaseCompleted = false;

  public isCustom: Signal<boolean> = computed(() => !!this.data().custom);
  public type: Signal<AssessmentType> = computed(() => (this.isCustom() ? 'CUSTOM' : this.data().standard!.type));
  public typeShortName: Signal<string> = computed(() =>
    this.isCustom() ? this.customAssessment!.shortName : this.data().standard!.type
  );

  public assessmentIndex = signal<number>(0);

  public currentDate: Signal<string> = computed(() => this.currentAssessment()?.dateIso ?? 'not found');
  public isTwoSided: Signal<boolean> = computed(() =>
    this.isCustom()
      ? this.customAssessment!.sideDistinction
      : ['ARAT', 'NHPT', 'BBT'].includes(this.currentAssessment()?.type ?? '')
  );

  public hasNext: Signal<boolean> = computed(() => this.assessmentIndex() > 0);
  public hasPrevious: Signal<boolean> = computed(
    () => this.assessmentIndex() < this.assessmentFilteredList().length - 1
  );

  public currentAssessment: Signal<Assessment> = computed(() => {
    const index = this.assessmentIndex();
    const assessment = this.assessmentFilteredList()[index];
    if (assessment === undefined) {
      // @TODO: z.b. nach dem Löschen...
    }
    // whenever this changes, we also update url params!
    if (this.initPhaseCompleted && assessment) {
      if (assessment.customId) {
        this.updateUrlParams({ cId: assessment.customId.toString(), dId: assessment._documentId! });
      } else {
        this.updateUrlParams({ type: assessment.type, dId: assessment._documentId! });
      }
    }
    return assessment;
  });

  public currentAssessmentLink: Signal<string> = computed(() => {
    const assessment = this.currentAssessment();
    if (assessment) {
      if (assessment.customId) {
        const url = this.router
          .createUrlTree(['../../assessments', assessment.type.toLowerCase(), assessment._documentId], {
            relativeTo: this.route,
            queryParams: { id: assessment.customId },
          })
          .toString();
        const fullUrl = `${window.location.origin}${url}`;
        return fullUrl ?? '';
      } else {
        const url = this.router
          .createUrlTree(['../../assessments', assessment.type.toLowerCase(), assessment._documentId], {
            relativeTo: this.route,
          })
          .toString();

        const fullUrl = `${window.location.origin}${url}`;
        return fullUrl ?? '';
      }
    } else {
      return '';
    }
  });

  public currentAssessmentRouterLink: Signal<any[]> = computed(() => {
    const assessment = this.currentAssessment();
    if (assessment) {
      const baseLink = ['../../assessments', assessment.type.toLowerCase(), assessment._documentId];
      return baseLink ?? [];
    } else {
      return [];
    }
  });

  public currentAssessmentQueryParams: Signal<any> = computed(() => {
    const assessment = this.currentAssessment();
    return assessment?.customId ? { id: assessment.customId } : {};
  });
  public currentAssessmentImageUrls: Signal<CustomFile[]> = computed(() => {
    const current = this.currentAssessment();
    // console.log('chris ', current);

    if (current?.type === 'CUSTOM') {
      const custom = current.data as CustomData;
      if (custom.custom.files && custom.custom.files?.length > 0) {
        return custom.custom.files;
      }
    }
    return [];
  });
  public currentAssessmentTugValue: Signal<number> = computed(() => {
    const current = this.currentAssessment();
    if (current?.type === 'TUG') {
      const data = current.data as TugData;
      const subTest: SubTestDataSet<SingleNumberDataSet> = data.tug;
      return subTest.data[0].value ?? -1;
    }
    return -1;
  });
  public currentNhptAssessmentShowSubtest: Signal<boolean> = computed(() => {
    const current = this.currentAssessment();
    // console.log('chris currentNhptAssessmentShowSubtest', current);

    if (current?.type === 'NHPT') {
      const nhpt = current.data as NhptData;
      return nhpt.nhpt.score_left === 1 || nhpt.nhpt.score_right === 1;
    }
    return false;
  });

  $routeParams = toSignal(this.route.queryParamMap);

  constructor() {
    const observeInitialAssesment = toObservable(this.currentAssessment);
    observeInitialAssesment.pipe(take(1)).subscribe(assessment => {
      // console.log('chris observeInitialAssesment', assessment);
      // console.log('chris constructor DetailsComponent', this.$routeParams(), assessment);
      this.initPhaseCompleted = true;
      if (assessment) {
        const assessmentType: AssessmentType | undefined = this.$routeParams()?.get('type') as AssessmentType;
        const customId: number = Number.parseFloat(this.$routeParams()?.get('cId') ?? '-1');
        const documentId: string | undefined = this.$routeParams()?.get('dId') as string;
        if (assessment.type === assessmentType || assessment.customId === customId) {
          const foundIndex: number = this.assessmentFilteredList().findIndex(item => item._documentId === documentId);
          // console.log('chris foundindex ' + foundIndex);
          if (foundIndex > -1) {
            this.assessmentIndex.set(foundIndex);
          }
        }
      }
    });
  }

  navigateToAssessment() {
    const assessment = this.currentAssessment();
    // console.log('ass', assessment);
    if (assessment) {
      if (assessment.customId) {
        this.router.navigate(['../../assessments/' + assessment.type.toLowerCase() + '/' + assessment._documentId], {
          relativeTo: this.route,
          queryParams: { id: assessment.customId },
        });
      } else {
        this.router.navigate(['../../assessments/' + assessment.type.toLowerCase() + '/' + assessment._documentId], {
          relativeTo: this.route,
        });
      }
    }
  }

  onNext(): void {
    // Liste ist so sortiert, daß der aktuellste Eintrag auf Index 0 steht. Daher ist alles umgekehrt
    if (this.assessmentIndex() > 0) {
      this.assessmentIndex.update(index => index - 1);
    }
  }
  onPrevious(): void {
    // Liste ist so sortiert, daß der aktuellste Eintrag auf Index 0 steht. Daher ist alles umgekehrt
    if (this.assessmentIndex() < this.assessmentFilteredList().length - 1) {
      this.assessmentIndex.update(index => index + 1);
    }
  }

  openImageInNewTab(imageUrl: string) {
    window.open(imageUrl, '_blank');
  }

  updateUrlParams(params: { [key: string]: string }) {
    // Hole die aktuelle URL ohne Parameter
    // const path = this.route.snapshot.routeConfig?.path;
    const fullPath = this.router.url.split('?')[0]; // Nur den Pfad ohne bestehende Query-Parameter

    // Erzeuge einen neuen Query-String
    const queryParams = new URLSearchParams(params).toString();

    // Update die URL mit den neuen Parametern
    this.location.replaceState(fullPath ?? '', queryParams);
  }
}
